import * as ReactIs from 'react-is'

/**
 * Checks the completeness and correctness of the loaded module setup.
 *
 * @param {object} module - The loaded module
 * @param {string} [name] - The module name (needed to display error messages)
 *
 * @throws {Error} Incorrect setup error description
 */
export function checkModuleSetup(module, name = '') {
    const messages = []

    // Obtain the main React component of the module (each module must have a 'View' React component)
    const View = module.View
    if (!View) {
        messages.push('Class "View" not found in module' + (name ? ` "${name}"` : ''))
    } else if (!ReactIs.isValidElementType(View)) {
        messages.push('Export "View" must be a valid React component in module' + (name ? ` "${name}"` : ''))
    }

    // Get the Redux reducers definition (each module must have a 'reducers' object whose values are reducers)
    const reducers = module.reducers
    if (reducers) {
        if (typeof reducers !== 'object') {
            messages.push('Export "reducers" must be an object in module' + (name ? ` "${name}"` : ''))
        } else if (!Object.keys(reducers).reduce((prev, curr) => prev && typeof reducers[curr] === 'function', true)) {
            messages.push(
                'All values of the "reducers" object must be reducer functions in module' + (name ? ` "${name}"` : '')
            )
        }
    }

    // Get the optional refresh schedules
    const schedules = module.schedules
    if (schedules !== undefined && !(schedules instanceof Function)) {
        try {
            verifySchedules(schedules, name)
        } catch (error) {
            messages.push(error.message)
        }
    }

    // Get the optional module translation context
    const contextCode = module.contextCode
    if (contextCode != null) {
        if (typeof contextCode !== 'string' || contextCode.length === 0) {
            messages.push('Export "contextCode" must be a non-empty string in module' + (name ? ` "${name}"` : ''))
        }
    }

    if (messages.length > 0) {
        throw new Error(messages.join(', '))
    }
}

/**
 * Verifies the completeness and correctness of a loaded module and returns a shallow copy of it.
 *
 * @param {string} name - The module name (needed to display error messages)
 * @param {object} module - The loaded module
 *
 * @return {object} The verified module
 *
 * @throws {Error} Incorrect setup error description
 */
export function verifyModule(name, module) {
    checkModuleSetup(module, name)
    return Object.assign({}, module)
}

export function verifySchedules(schedules, moduleName) {
    const messages = []
    if (typeof schedules !== 'object' || Object.keys(schedules).length === 0) {
        messages.push(
            'Export "schedules" must be a non-empty object in module' + (moduleName ? ` "${moduleName}"` : '')
        )
    } else {
        // Check if each store key maps to a path (of type string)
        Object.keys(schedules).forEach(store => {
            if (typeof schedules[store] !== 'string') {
                messages.push(
                    'Schedule "${store}" must map to a string in module' + (moduleName ? ` "${moduleName}"` : '')
                )
            }
        })
    }
    if (messages.length > 0) {
        throw new Error(messages.join(', '))
    }
}
